import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { JobCredits } from '@/data/jobOptions';

export function useJobCredits() {
  const { user, isPlus } = useAuth();

  return useQuery({
    queryKey: ['job-credits', user?.id],
    queryFn: async () => {
      if (!user) return null;

      // First get the company for this user
      const { data: company, error: companyError } = await supabase
        .from('companies')
        .select('id')
        .eq('owner_user_id', user.id)
        .single();

      if (companyError || !company) return null;

      // Get or create credits
      let { data: credits, error: creditsError } = await supabase
        .from('job_credits')
        .select('*')
        .eq('company_id', company.id)
        .single();

      if (creditsError && creditsError.code === 'PGRST116') {
        // Credits don't exist, create them
        const { data: newCredits, error: createError } = await supabase
          .from('job_credits')
          .insert({
            company_id: company.id,
            credits_available: 2,
            credits_used: 0,
            monthly_limit: 2,
          })
          .select()
          .single();

        if (createError) throw createError;
        credits = newCredits;
      } else if (creditsError) {
        throw creditsError;
      }

      return credits as JobCredits;
    },
    enabled: !!user && isPlus(),
    staleTime: 1000 * 30, // 30 seconds
  });
}

export function useDecrementCredits() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (companyId: string) => {
      // First get current credits
      const { data: current, error: fetchError } = await supabase
        .from('job_credits')
        .select('credits_available, credits_used')
        .eq('company_id', companyId)
        .single();

      if (fetchError) throw fetchError;

      const { data, error } = await supabase
        .from('job_credits')
        .update({
          credits_available: Math.max(0, current.credits_available - 1),
          credits_used: current.credits_used + 1,
        })
        .eq('company_id', companyId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-credits', user?.id] });
    },
  });
}
